import React from 'react';
import { 
  Paper, 
  Typography, 
  Grid, 
  Box, 
  Chip, 
  Button,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDexService } from '../../../contexts/DexServiceContext';
import { LogLevel } from '../../../types/dex';

/**
 * 服务状态组件
 * 显示当前DexService的配置和状态信息
 */
const ServiceStatus: React.FC = () => {
  const { dexService, config, isInitialized, resetDexService, addLog } = useDexService();
  const navigate = useNavigate();
  
  // 处理重置服务
  const handleReset = () => {
    addLog({
      level: LogLevel.WARNING,
      message: '正在重置DEX服务...'
    });
    resetDexService();
    addLog({
      level: LogLevel.SUCCESS,
      message: 'DEX服务已重置'
    });
  };
  
  // 跳转到配置页面
  const handleEditConfig = () => {
    addLog({
      level: LogLevel.INFO,
      message: '正在跳转到配置页面'
    });
    navigate('/dex-config');
  };
  
  // 记录初始化状态
  React.useEffect(() => {
    if (isInitialized && config) {
      addLog({
        level: LogLevel.SUCCESS,
        message: 'DEX服务已初始化',
        data: {
          network: config.rpc.name,
          chainId: config.rpc.chainId,
          dex: config.dex.router.name,
          wallets: config.wallets.length
        }
      });
    } else {
      addLog({
        level: LogLevel.WARNING,
        message: 'DEX服务未初始化，请先完成配置'
      });
    }
  }, [isInitialized, config]);
  
  if (!isInitialized || !config) {
    return (
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
        <Typography variant="h6">服务未初始化</Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleEditConfig}
          sx={{ mt: 1 }}
        >
          前往配置页面
        </Button>
      </Paper>
    );
  }
  
  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        DEX服务状态
        <Chip 
          label="已运行" 
          color="success" 
          size="small" 
          sx={{ ml: 1 }}
        />
      </Typography>
      
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 1.5, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              网络信息
            </Typography>
            <Box>
              <Typography variant="body2" component="div">
                <strong>名称:</strong> {config.rpc.name}
              </Typography>
              <Typography variant="body2" component="div">
                <strong>链ID:</strong> {config.rpc.chainId}
              </Typography>
              <Typography variant="body2" component="div" 
                sx={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap' 
                }}
              >
                <strong>RPC URL:</strong> {config.rpc.url}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 1.5, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              DEX信息
            </Typography>
            <Box>
              <Typography variant="body2" component="div">
                <strong>名称:</strong> {config.dex.router.name}
              </Typography>
              <Typography variant="body2" component="div"
                sx={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap' 
                }}
              >
                <strong>路由器地址:</strong> {config.dex.router.address}
              </Typography>
              {config.dex.factory?.address && (
                <Typography variant="body2" component="div"
                  sx={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap' 
                  }}
                >
                  <strong>工厂地址:</strong> {config.dex.factory.address}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 1.5, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              钱包信息
            </Typography>
            <Box>
              <Typography variant="body2" component="div">
                <strong>钱包数量:</strong> {config.wallets.length}
              </Typography>
              {config.wallets.length > 0 && (
                <Typography variant="body2" component="div"
                  sx={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap' 
                  }}
                >
                  <strong>首个钱包地址:</strong> {config.wallets[0].address}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button 
          variant="outlined" 
          color="primary"
          onClick={handleEditConfig}
        >
          编辑配置
        </Button>
        <Button 
          variant="outlined" 
          color="error"
          onClick={handleReset}
        >
          重置服务
        </Button>
      </Stack>
    </Paper>
  );
};

export default ServiceStatus;
